import 'typeface-roboto';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AuthorDetails } from './pages/admin/authors/authorDetails.js';
import { AuthorCreate } from './pages/admin/authors/authorCreate.js';
import { RequireAuth, RequireAdmin } from './components/privateRoute';

import Copyright from './components/Copyright';
import NavBar from './components/NavBar';

import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import './App.css';

import LandingPage from './pages/landingPage';
import Terms from './pages/terms';
import SignOut from './pages/signOut';
import SignIn from './pages/signIn';
import Registration from './pages/register';
import Catalogue from './pages/catalogue';
import RegistrationDone from './pages/registerDone';
import About from './pages/about';

import { HelmetProvider } from 'react-helmet-async';
// import { setAuthenticated } from './store/actions/auth';

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { selectCurrentUser } from './store/authSlice';

import { createTheme, ThemeProvider } from '@material-ui/core';
import ThemePicker from './components/ThemePicker';
import Help from './pages/help';
import { BookDetails } from './pages/bookDetails';
import NewTitles from './pages/newTitles';
import MyBooks from './pages/mybooks';
import Contact from './pages/contactPage';
import Profile from './pages/profilePage';
import ForgotPassword from './pages/forgotPassword';
import ResetPassword from './pages/resetPassword';
import { CreatedBy } from './components/CreatedBy';
import { Authors } from './pages/admin/authors/authors';
import { Actors } from './pages/admin/actors/actors.js';
import { ActorCreate } from './pages/admin/actors/actorCreate.js';
import { ActorDetails } from './pages/admin/actors/actorDetails.js';
import { Categories } from './pages/admin/categories/categories.js';
import { CategoryDetails } from './pages/admin/categories/categoryDetails.js';
import { CategoryCreate } from './pages/admin/categories/categoryCreate.js';
import { Stats } from './pages/admin/stats/index.js';
import { MonthlyDownloads } from './pages/admin/stats/monthlyDownloads.js';
import { MonthlyDownloadsUser } from './pages/admin/stats/monthlyDownloadsUser.js';
import { MonthlyDownloadsBooks } from './pages/admin/stats/monthlyDownloadsBooks.js';
import AdminCatalogue from './pages/admin/books/catalogue.js';
import { CatalogueCreate } from './pages/admin/books/bookCreate.js';
import { CatalogueEdit } from './pages/admin/books/bookEdit.js';
import { CatalogueDelete } from './pages/admin/books/bookDelete.js';
import { Users } from './pages/admin/users/users.js';
import { UserEdit } from './pages/admin/users/userEdit.js';
import { UserDetails } from './pages/admin/users/userDetails.js';
import { EmailTemplateEditPage } from './pages/admin/EmailTemplateEditPage/index.js';
import { AdminNewTitles } from './pages/admin/books/newTitles.js';
import { ArchiveMonitor } from './pages/admin/books/archiveMonitor.js';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
	},
	appBarSpacer: theme.mixins.toolbar,
	menuButton: {
		marginRight: 36,
	},
	content: {
		width: '100%',
		alignItems: 'center',
		direction: 'row',
	},
	container: {
		maxWidth: 'lg',
		width: '100%',
	},
}));

function App() {
	const classes = useStyles();

	const user = useSelector(selectCurrentUser);
	const isAuthenticated = user.isAuthenticated;
	const isAdmin = user.isAdmin;
	const preferences = useSelector((state) => state.preference);

	const theme = useMemo(() => {
		return createTheme({
			palette: {
				primary: {
					main: preferences.color,
				},
				secondary: {
					main: preferences.backgroundColor,
				},
				background: {
					paper: preferences.backgroundColor,
					default: preferences.contrastColor,
				},
				text: {
					primary: preferences.color,
					secondary: preferences.color,
				},
			},
			typography: {
				fontWeightLight: 400,
				fontWeightRegular: 600,
				fontWeightMedium: 700,
				fontWeightBold: 800,
				fontSize: preferences.fontSize,
			},
		});
	}, [preferences]);

	return (
		<Router>
			<HelmetProvider>
				<ThemeProvider theme={theme}>
					<div className={classes.root}>
						<NavBar isAuthenticated={isAuthenticated} isAdmin={isAdmin} />
						<CssBaseline />
						<main className={classes.content}>
							<div className={classes.appBarSpacer} />
							<Container maxWidth="lg" className={classes.container}>
								<ThemePicker />
								<Routes>
									<Route path="/" element={<LandingPage />} />
									<Route path="/about" element={<About />} />
									<Route path="/contact" element={<Contact />} />
									<Route
										path="/profile"
										element={
											<RequireAuth>
												<Profile />
											</RequireAuth>
										}
									/>
									<Route
										path="/change-password"
										element={
											<RequireAuth>
												<Profile />
											</RequireAuth>
										}
									/>
									<Route
										path="/forgot-password"
										element={<ForgotPassword />}
									/>
									<Route
										path="/reset-password"
										element={<ResetPassword />}
									/>
									<Route
										path="/reset-password/:token"
										element={<ResetPassword />}
									/>
									<Route path="/sign-in" element={<SignIn />} />
									<Route path="/terms" element={<Terms />} />
									<Route path="/sign-out" element={<SignOut />} />
									<Route path="/sign-up" element={<Registration />} />
									<Route path="/catalogue" element={<Catalogue />} />
									<Route
										path="/mybooks"
										element={
											<RequireAuth>
												<MyBooks />
											</RequireAuth>
										}
									/>
									<Route path="/newtitles" element={<NewTitles />} />
									<Route path="/catalogue/:id" element={<BookDetails />} />
									<Route path="/sign-up-done" element={<RegistrationDone />} />
									<Route path="/help" element={<Help />} />

									<Route
										path="/admin/archive-monitor"
										element={
											<RequireAdmin>
												<ArchiveMonitor />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/catalogue"
										element={
											<RequireAdmin>
												<AdminCatalogue />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/newTitles"
										element={
											<RequireAdmin>
												<AdminNewTitles />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/catalogue/create"
										element={
											<RequireAdmin>
												<CatalogueCreate />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/catalogue/edit/:id"
										element={
											<RequireAdmin>
												<CatalogueEdit />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/catalogue/delete/:id"
										element={
											<RequireAdmin>
												<CatalogueDelete />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/authors/:id"
										element={
											<RequireAdmin>
												<AuthorDetails />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/authors/create"
										element={
											<RequireAdmin>
												<AuthorCreate />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/users"
										element={
											<RequireAdmin>
												<Users />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/users/edit/:id"
										element={
											<RequireAdmin>
												<UserEdit />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/users/:id"
										element={
											<RequireAdmin>
												<UserDetails />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/mail"
										element={
											<RequireAdmin>
												<EmailTemplateEditPage />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/downloadCount"
										element={
											<RequireAdmin>
												<MonthlyDownloads />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/downloadCountUsers"
										element={
											<RequireAdmin>
												<MonthlyDownloadsUser />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/dwonloadCountBooks"
										element={
											<RequireAdmin>
												<MonthlyDownloadsBooks />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/authors"
										element={
											<RequireAdmin>
												<Authors />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/actors"
										element={
											<RequireAdmin>
												<Actors />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/actors/create"
										element={
											<RequireAdmin>
												<ActorCreate />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/actors/:id"
										element={
											<RequireAdmin>
												<ActorDetails />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/categories"
										element={
											<RequireAdmin>
												<Categories />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/categories/create"
										element={
											<RequireAdmin>
												<CategoryCreate />
											</RequireAdmin>
										}
									></Route>
									<Route
										path="/admin/categories/:id"
										element={
											<RequireAdmin>
												<CategoryDetails />
											</RequireAdmin>
										}
									></Route>

									<Route
										path="/admin/stats"
										element={
											<RequireAdmin>
												<Stats />
											</RequireAdmin>
										}
									></Route>
								</Routes>
								<Box pt={4}>
									<Copyright />
									<CreatedBy />
								</Box>
							</Container>
						</main>
					</div>
				</ThemeProvider>
			</HelmetProvider>
		</Router>
	);
}

export default App;
